import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CONTENT_GROUP_DUPLICATE } from '../../constants';
import { useDetectOutsideClick } from '../../utils/useDetectOutsideClick';
import { Image, FormControl, FormSelect, Button, Dropdown, Spinner, Form } from 'react-bootstrap';
import useCardDuplicateHook from './useCardDuplicateHook';
import { ListItemChecked, ListItemUnchecked, RoundedCheckBox } from '../icons';
import { CircleAddIcon } from '../icons/CircleAddIcon';
import CircleMinusIcon from '../icons/CircleMinusIcon';
import useDetectOutsideHook from './useDetectOutsideHook';
import { FormDropdown } from '../form/FormDropdown';
import { BasicSpinner } from '../BasicSpinner';
import { CircleCheckBox } from '../form/CircleCheckBox';

const Checkbox = ({ label, checked, onClick, name, disabled }) => (
  <div className="d-flex align-items-center cursor-pointer field-checkbox"
    onClick={(e) => disabled ? e.preventDefault() : onClick(e, name)}
  >

    <CircleCheckBox 
        isChecked={checked}
        height={18}
        width={18}
    />
    <input
        className="form-check-input d-none"
        type="checkbox"
        defaultChecked={checked}
        checked={checked}
        name={name}
        onClick={(e) => onClick(e, name)}
    />
    <label className="form-check-label cursor-pointer ms-2 mt-0">
      {label}
    </label>
  </div>
);

const CardDuplicatePanel = () => {
    const dispatch = useDispatch();
    const { innerWidth, innerHeight } = window;
    const { isModalOpen, boundary, error } = useSelector(state => state.card_duplicate);
    const titleRef = useRef(null);
    const stageRef = useRef(null);
    const {
        name,
        handleNameChange,
        includedFields,
        stage_id,
        toggleIncludedFields,
        handleStageChange,
        copies,
        handleCopiesChange,
        handleCancel,
        participants,
        labels,
        start_date,
        due_date,
        images,
        description,    
        lists,
        reminder,
        handleSubmit,
        dropdownMaxHeight
    } = useCardDuplicateHook({
        isModalOpen,
        titleRef,
        stageRef
    });
    const panelRef = useRef(null);
    const { loading } = useSelector(state => state.card_duplicate);
    const options = useSelector(state => state.card_duplicate.stages) // Temporary fix, should be replaced with actual stage options content_group.allowed_stages
    const max_copy = CONTENT_GROUP_DUPLICATE.MAX_COPY;

    useDetectOutsideHook({
        ref: panelRef,
        isModalOpen,
        dispatch,
        handleCancel
    });

    if (!isModalOpen) {
        return null;
    }

    const getDuplicatePanelStyle = () => {
        let styles = {}
        const height = 698;
        const width = 280;

        let desiredLeft = boundary?.left;
        let desiredTop = boundary?.top;

        if (desiredLeft + width > innerWidth)
            desiredLeft = innerWidth - boundary.left - width - 10;

        if (desiredLeft < 10){
            desiredLeft = boundary.left - width;
        }

        if(desiredLeft < 0)
            desiredLeft = 60;

        if (desiredTop + height > innerHeight - boundary.top)
            desiredTop = innerHeight - boundary.top - height - 10;

        if (desiredTop < 0){
            desiredTop = boundary.top - height;
        }

        if (desiredTop < 10)
            desiredTop = 60;

        styles.left = `${desiredLeft}px`;
        styles.top = `${desiredTop}px`;

        return styles;
    }

    const copiesButton = () => {
        const black = '#0F172A';
        const white = "#D9D9D9";

        return (
            <div className='button-group d-flex align-items-center'>
                <div className='mx-1 cursor-pointer' onClick={(e) => loading ? e.preventDefault() : handleCopiesChange(e, 'decrement')}>
                    <CircleMinusIcon
                        color={ copies > 1 && !loading ? black : white }
                        height={12} width={12}
                    />
                </div>

                <div className='mx-1 cursor-pointer' onClick={(e) => loading ? e.preventDefault() : handleCopiesChange(e, 'increment')}>
                    <CircleAddIcon color={ copies >= max_copy || loading ? white : black } height={12} width={12} />
                </div>
            </div>
        );
    }
    
    return (
        <div 
            className='card-duplicate-panel position-absolute' 
            style={getDuplicatePanelStyle()}
            ref={panelRef}
        >
            {
                loading &&
                    <div className='z-1 position-absolute w-100 h-100 d-flex align-items-center justify-content-center top-0 start-0 bg-black-60'>
                        <BasicSpinner variant="light" />
                    </div>     
            }
            <div className='position-relative'>

                <div className='card-duplicate-panel__title'>
                Copy Card
                </div>

                <Form
                    onSubmit={ (e) => loading ? null : handleSubmit(e) }
                >           
                    <div>
                        <label className='field-label'>Card Title</label>
                        <FormControl 
                            defaultValue={name}
                            onChange={handleNameChange}
                            disabled={loading}
                            ref={titleRef}
                        />            
                    </div>

                    <div className='mb-6px'>
                        <label className='field-label'>Contents</label>

                        <Checkbox
                            label="Participants"
                            checked={participants}
                            onClick={toggleIncludedFields}
                            name="participants"
                            disabled={loading}  
                        />

                        <Checkbox
                            label="Labels"
                            checked={labels}
                            onClick={toggleIncludedFields}
                            name="labels"
                            disabled={loading}
                        />

                        <Checkbox
                            label="Start Date"
                            checked={start_date}
                            onClick={toggleIncludedFields}
                            name="start_date"
                            disabled={loading}
                        />

                        <Checkbox
                            label="Due Date"
                            checked={due_date}
                            onClick={toggleIncludedFields}
                            name="due_date"
                            disabled={loading}
                        />

                        <Checkbox
                            label="Reminder"
                            checked={reminder}
                            onClick={toggleIncludedFields}
                            name="reminder"
                            disabled={loading}
                        />

                        <Checkbox
                            label="Images"
                            checked={images}
                            onClick={toggleIncludedFields}
                            name="images"
                            disabled={loading}
                        />

                        <Checkbox
                            label="Description"
                            checked={description}
                            onClick={toggleIncludedFields}
                            name="description"
                            disabled={loading}
                        />

                        <Checkbox
                            label="Lists"
                            checked={lists}
                            onClick={toggleIncludedFields}
                            name="lists"
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className='field-label'>Stage</label>
                        <div>
                            <FormDropdown
                                options={options}
                                selected_option_id={stage_id}
                                onSelect={handleStageChange}
                                disabled={loading}
                                dropdownMenuRef={stageRef}
                                menuStyle={{ maxHeight: dropdownMaxHeight, overflowY: 'auto' }}
                            />
                        </div>
                    </div>

                    <div>
                        <label className='field-label'>Number of copies</label>

                        <div className='d-flex align-items-center justify-content-between form-control border-0'>
                            <div className='bg-transparent'>
                                {copies}
                            </div>
                            {copiesButton()}
                        </div>
                    </div>
                    <div className='d-grid'>
                        <Button
                            variant="success"
                            type='submit'
                            disabled={loading}
                            className='mb-6px fw-500'
                        >
                            Copy
                        </Button>

                        <Button
                            variant="blank"
                            onClick={handleCancel}
                            type='button'
                            disabled={loading}
                            className='fw-500'
                        >
                            Cancel
                        </Button>
                    </div>
                </Form>

            </div>
            
            
        </div>
    );
};

export default CardDuplicatePanel;